import { LayoutGrid } from 'lucide-react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/lib/auth';
import { usePassbook } from '@/shared/lib/passbook';
import { canAccessTenantPath, firstBasePath } from '@/shared/lib/navigation';
import { canAccessPassbook } from '@/shared/lib/passbook-access';
import { EmptyState } from '@/shared/components/empty-state';
import { FullPageLoader } from '@/shared/components/full-page-loader';

/** Bloqueia rotas de loja conforme a role (espelha menu do legado). */
export function TenantAccessGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const { passbook } = usePassbook();
  const location = useLocation();

  if (isLoading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;

  // Loja que não é da conta: cair na escolha, e não na loja do vizinho.
  if (!canAccessPassbook(user, passbook._id)) return <Navigate to="/selecionar-loja" replace />;

  if (!canAccessTenantPath(user.role, location.pathname, passbook, user.email)) {
    if (user.role === 'Base') {
      const next = firstBasePath(passbook._id, passbook);
      if (!next || location.pathname === next) {
        return (
          <EmptyState
            icon={LayoutGrid}
            title="Nenhuma página liberada"
            description="O master ainda não liberou páginas para este usuário."
          />
        );
      }
      return <Navigate to={next} replace />;
    }
    const hasMetabase = Boolean(passbook.metabase && passbook.metabase.length > 0);
    const fallback =
      user.role === 'Validator'
        ? `/loja/${passbook._id}/operador`
        : user.role === 'Campaign'
          ? `/loja/${passbook._id}/push-marketing/campanhas`
          : user.role === 'Operator'
            ? hasMetabase
              ? `/loja/${passbook._id}`
              : `/loja/${passbook._id}/leads`
            : user.role === 'Manager'
              ? `/loja/${passbook._id}`
              : `/loja/${passbook._id}`;
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}
